import {
  Box,
  Card,
  CardContent,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  TextField,
} from '@material-ui/core'
import {
  Clear as ClearIcon,
  NotificationsOutlined,
  Search as SearchIcon,
} from '@material-ui/icons'
import { AppPagination } from '../../components/app-pagination'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EmptySearchResultComponent } from '../../components/empty-search-result-component/empty-search-result.component'
import { UserNotification } from '../../store/notifications/types'
import { useNotificationPage } from './notification-page.hook'
import { NotificationPlaceholder } from './notifications-item/notification-placeholder.component'
import { NotificationsItemComponent } from './notifications-item/notifications-item-component'
import { useNotificationPageStyle } from './notifications-page.styles'
import { PageWrapper } from '../../components/page-wrapper'
import { BreadcrumbItem } from '../../components/breadcrumbs'
import { LabeledSelect } from '../../shared/labeled-select'

/**
 * Read status types for filtering notifications
 * 0: All notifications
 * 1: Unread notifications
 * 2: Read notifications
 */
const ReadType = {
  0: 0,
  1: 1,
  2: 2,
}

export const NotificationsPage = () => {
  const classes = useNotificationPageStyle()
  const { t } = useTranslation()

  // State for selected read status filter (All, Unread, Read)
  const [selectedTab, setSelectedTab] = useState<number>(0)
  // State for search query text
  const [searchQuery, setSearchQuery] = useState('')
  // State for current page number in pagination
  const [page, setPage] = useState(1)
  // Get notifications from custom hook
  const { notifications, loading, markAsReviewedHandler } =
    useNotificationPage()

  const notificationBreadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      { label: t('header.menu.search'), to: '/search' },
      { label: t('notifications.title') },
    ],
    [t]
  )

  const readTypeOptions = useMemo(
    () => [
      { value: ReadType['0'], label: t('notifications.all') },
      { value: ReadType['1'], label: t('notifications.unread') },
      { value: ReadType['2'], label: t('notifications.read') },
    ],
    [t]
  )

  const handleReadTypeChange = (newValue: number) => {
    setSelectedTab(newValue)
    setPage(1)
    setSearchQuery('')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  /**
   * Filters notifications based on their read status (All, Unread, or Read)
   * @returns Filtered array of notifications matching the selected read status
   */
  const filteredByReadType = () =>
    notifications.filter((item) => {
      // Show all notifications if "All" tab is selected
      if (selectedTab === ReadType['0']) return true
      // Show only unread notifications if "Unread" tab is selected
      else if (selectedTab === ReadType['1']) {
        return !item.reviewed ? true : false
      }
      // Show only read notifications if "Read" tab is selected
      else {
        return !item.reviewed ? false : true
      }
    })

  /**
   * Filters notifications by search query in subject and text fields
   * @param notifications - Array of notifications to filter
   * @param query - Search query string
   * @returns Filtered array of notifications matching the search query
   */
  const filterBySearchQuery = (
    notifications: UserNotification[],
    query: string
  ): UserNotification[] => {
    // Return all notifications if search query is empty
    if (!query.trim()) {
      return notifications
    }

    // Convert search query to lowercase for case-insensitive search
    const searchTerm = query.toLowerCase().trim()
    return notifications.filter((notification) => {
      // Search in both subject and text fields
      const subject = notification.subject?.toLowerCase() || ''
      const text = notification.text?.toLowerCase() || ''
      // Return true if search term is found in either field
      return subject.includes(searchTerm) || text.includes(searchTerm)
    })
  }

  /**
   * Combines filtering by read type and search query
   * @returns Filtered array of notifications matching both read status and search query
   */
  const getFilteredNotifications = (): UserNotification[] => {
    const filteredByType = filteredByReadType()
    return filterBySearchQuery(filteredByType, searchQuery)
  }

  /**
   * Paginates an array of notifications
   * @param array - Array of notifications to paginate
   * @param pageNumber - Current page number (1-based)
   * @param itemsPerPage - Number of items per page (default: 10)
   * @returns Sliced array containing items for the current page
   */
  function paginateArray(
    array: UserNotification[],
    pageNumber: number,
    itemsPerPage = 10
  ) {
    // Calculate start index for current page (0-based)
    const startIndex = (pageNumber - 1) * itemsPerPage
    // Calculate end index for current page
    const endIndex = startIndex + itemsPerPage
    // Return slice of array for current page
    return array.slice(startIndex, endIndex)
  }

  const markAsReviwed = (id: number) => {
    markAsReviewedHandler(id)
  }

  /**
   * Renders the list of notification items
   * Filters by read type and search query, applies pagination, and maps to notification components
   * @returns Array of Grid items containing notification components
   */
  const renderList = () => {
    const filteredNotifications = getFilteredNotifications()
    const _notifications = paginateArray(filteredNotifications, page)
    return _notifications.map(
      (notification: UserNotification, index: number) => (
        <Grid item xs={12} key={notification.userNotificationId || index}>
          <NotificationsItemComponent
            {...notification}
            markNotificationAsReviewed={() =>
              markAsReviwed(notification.userNotificationId)
            }
          />
        </Grid>
      )
    )
  }

  return (
    <>
      <PageWrapper
        title={t('notifications.title')}
        displayGoBackButton={false}
        breadcrumbItems={notificationBreadcrumbItems}
      >
        <LinearProgress
          className={
            loading
              ? classes.linearProgressVisible
              : classes.linearProgressHidden
          }
        />
        <Grid container spacing={1}>
            {/*Search*/}
            <Grid item xs={12} md={8} className={classes.searchControl}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder={t(
                  'notifications.searchPlaceholder',
                  'Search notifications...'
                )}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setPage(1) // Reset to first page when search query changes
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery('')}
                        className={classes.clearButton}
                        aria-label="clear search"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <LabeledSelect
                label={t('notifications.statusFilter')}
                value={selectedTab}
                options={readTypeOptions}
                onChange={handleReadTypeChange}
                idSuffix="notifications-read-status"
                className={classes.readStatusSelect}
                fluid
              />
            </Grid>

            {/*Notifications*/}
            <Grid item xs={12}>
              <Card className={classes.notificationsCard} elevation={0}>
                <CardContent className={classes.cardContent}>
                  {!getFilteredNotifications().length && !loading ? (
                    <Box className={classes.emptyStateContainer}>
                      <EmptySearchResultComponent
                        text={
                          searchQuery.trim()
                            ? t(
                                'notifications.noSearchResults',
                                'No notifications found'
                              )
                            : selectedTab === 1
                            ? t(
                                'notifications.noUnread',
                                'No unread notifications'
                              )
                            : selectedTab === 2
                            ? t('notifications.noRead', 'No read notifications')
                            : t('notifications.noNotifications')
                        }
                        icon={NotificationsOutlined}
                      />
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      {loading ? (
                        <PlaceholderArrayComponent></PlaceholderArrayComponent>
                      ) : (
                        renderList()
                      )}
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/*Pagination*/}
            {getFilteredNotifications().length > 10 && !loading && (
              <Grid item xs={12} justifyContent="center">
                <AppPagination
                  count={Math.ceil(getFilteredNotifications().length / 10)}
                  disabled={loading}
                  page={page}
                  onChange={(_, nextPage) => {
                    setPage(nextPage)
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                  }}
                />
              </Grid>
            )}
        </Grid>
      </PageWrapper>
    </>
  )
}

/**
 * Placeholder component that displays skeleton loaders while notifications are loading
 * Creates 12 placeholder items to simulate the notification list
 */
const PlaceholderArrayComponent: React.FC = () => {
  const placeholderArray = []
  //eslint-disable-next-line
  for (let i = 0; i < 12; i++) {
    placeholderArray.push(
      <Grid key={i} item xs={12}>
        <NotificationPlaceholder />
      </Grid>
    )
  }
  return <>{placeholderArray}</>
}
